// src/components/ui/Stepper.jsx
import React from "react";

export default function Stepper({
  steps = [],            // [{ key: 'borrower', label: 'Borrower type' }, ...]
  current = 0,           // 0-based active index
  onStepChange,          // optional (click to jump)
  className = "",
  allowForwardNav = false, // set true only if you want to allow clicking future steps
}) {
  // Highest index the user may click
  const maxEnabledIndex = allowForwardNav ? steps.length - 1 : Math.max(0, Math.min(current, steps.length - 1));

  return (
    <div className={`w-full overflow-x-auto overflow-y-hidden ${className}`}>
      {/* Center the stepper and constrain width on large screens */}
      <div className="w-full max-w-4xl mx-auto px-1 min-w-0">
        <ol className="flex items-center justify-center gap-2 sm:gap-4 md:gap-6">
          {steps.map((s, idx) => {
            const isDone = idx < current;
            const isActive = idx === current;
            const isLast = idx === steps.length - 1;
            const isEnabled = idx <= maxEnabledIndex;

            return (
              <li key={s.key} className="flex items-center">
                {/* Node + Label (button to allow going back; future steps disabled) */}
                <button
                  type="button"
                  title={s.label}
                  aria-label={s.label}
                  aria-current={isActive ? "step" : undefined}
                  disabled={!isEnabled}
                  onClick={() => {
                    if (!isEnabled) return;
                    if (onStepChange) onStepChange(idx);
                  }}
                  className={[
                    "flex flex-col items-center focus:outline-none",
                    isEnabled ? "cursor-pointer" : "cursor-not-allowed opacity-60"
                  ].join(" ")}
                >
                  <div
                    className={[
                      "grid place-items-center w-8 h-8 sm:w-9 sm:h-9 rounded-full border-2 flex-shrink-0",
                      isDone
                        ? "bg-sky-500 border-sky-500 text-white"
                        : isActive
                        ? "bg-white border-sky-500 text-sky-700 shadow-sm"
                        : "bg-white border-slate-300 text-slate-500",
                    ].join(" ")}
                  >
                    {isDone ? (
                      <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" strokeWidth="2">
                        <path d="M20 6L9 17l-5-5" />
                      </svg>
                    ) : (
                      <span className="font-semibold">{idx + 1}</span>
                    )}
                  </div>

                  <div
                    className={[
                      "mt-1.5 sm:mt-2 text-[11px] sm:text-[13px] md:text-sm font-semibold text-center max-w-[5rem] sm:max-w-[9rem] md:max-w-[12rem] truncate",
                      isActive ? "text-sky-700" : "text-slate-600",
                    ].join(" ")}
                  >
                    {s.label}
                  </div>
                </button>

                {/* Connector with fixed width so layout stays centered */}
                {!isLast && (
                  <div className="mx-1 sm:mx-3 md:mx-4 flex-shrink-0">
                    <div className="w-8 sm:w-16 md:w-20 lg:w-28 h-[2px] bg-slate-200 rounded relative overflow-hidden">
                      <div
                        className={[
                          "absolute left-0 top-0 h-[2px] rounded transition-all",
                          // Fill the rail up to the current step
                          (idx < current) ? "bg-sky-400 w-full" : (idx === current ? "bg-sky-300 w-1/2" : "bg-slate-200 w-0")
                        ].join(" ")}
                      />
                    </div>
                  </div>
                )}
              </li>
            );
          })}
        </ol>
      </div>
    </div>
  );
}
